import { Box, Container, IconButton, Stack, useMediaQuery } from '@mui/material';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/router';
import { SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import SwiperCarousel from '../../shared/SwiperCarousel';
import { useToggle } from '../../../hooks/useToggle';
import { addAddOn, getPrepayAddons, onLoadPrepayAddons, removeAddOn } from './prepayMobile.util';
import EirOfferCard from '../../eir-offer-card/EirOfferCard';
import EirSingleOfferCard from '../../eir-offer-card/EirSingleOfferCard';
import useMaxHeightCard from '../../eir-offer-card/offer-cart.util';
import styles from './styles';
import RootContext from '../../../../util/root.context';
import ErrorModal from '../../eir-error-modal/ErrorModal';
import ApiFailureModal from '../../eir-error-modal/ApiFailureModal';

/**
 * @name PrepayMobileOfferContainer - jsx component for compatible addons
 * @param {object} cmsData - Strapi data
 * @param {Array} addons - compatible addons returned
 * @returns {JSX.Element} - Returns compatible addons component
 */
const PrepayMobileOfferContainer = ({ prepaySIM }) => {
  const theme = useTheme();
  const style = styles(theme);
  const graphql = useApolloClient();
  const router = useRouter();
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const md = useMediaQuery(theme.breakpoints.down('md'));
  const xs = useMediaQuery(theme.breakpoints.down('sm'));
  const [leftArrowDisabled, setLeftArrowDisabled] = useState(true);
  const [RightArrowDisabled, setRightArrowDisabled] = useState(false);
  const { state: rootState, dispatch } = useContext(RootContext);
  const [showLoader, setShowLoader] = useState('');
  const [offerSelected, setOfferSelected] = useState(null);
  const useMaxHeight = useMaxHeightCard();
  const swiperRef = useRef(null);
  const [openError, setOpenError] = useState(false);
  const [errorModalOccurence, setErrorModalOccurence] = useState(0);
  const errorModalMaxOccurence = 1;
  const errorModal = useToggle(false);
  const [onLoadError, setOnLoadError] = useState(false);
  const openAPIFailureModal = useToggle(false);
  const showModal = useToggle(false);
  const showSessionExpiredModal = useToggle(false);
  const handleOk = () => {
    openAPIFailureModal.disable();
  };

  const handleLeftClick = useCallback(() => {
    if (!swiperRef.current) return;
    swiperRef.current.swiper.slidePrev();
  }, []);

  const handleRightClick = useCallback(() => {
    if (!swiperRef?.current) return;
    swiperRef.current.swiper.slideNext();
  }, []);

  const checkArrowsStatus = () => {
    if (swiperRef?.current?.swiper?.isBeginning) {
      setLeftArrowDisabled(true);
    }
    if (swiperRef?.current?.swiper?.isEnd) {
      setRightArrowDisabled(true);
    }
    if (!swiperRef?.current?.swiper?.isBeginning) {
      setLeftArrowDisabled(false);
    }
    if (!swiperRef?.current?.swiper?.isEnd) {
      setRightArrowDisabled(false);
    }
  };

  const handleKeydown = e => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      e.target.click();
    }
  };

  const updatePaginationDots = swiper => {
    const paginationDots = swiper.el?.querySelectorAll('.swiper-pagination-bullet');
    if (!paginationDots || !paginationDots.length) return;

    const activeIndex = swiper.realIndex ?? swiper.activeIndex ?? 0;

    paginationDots.forEach((dot, i) => {
      dot.setAttribute('role', 'button');
      dot.setAttribute('tabindex', '0');

      dot.removeEventListener('keydown', handleKeydown);
      dot.addEventListener('keydown', handleKeydown);

      if (i === activeIndex) {
        dot.setAttribute('aria-label', 'Current plan');
      } else if (i < activeIndex) {
        dot.setAttribute('aria-label', 'Previous plan');
      } else {
        dot.setAttribute('aria-label', 'Next plan');
      }
    });
  };

  useEffect(() => {
    checkArrowsStatus();
  }, [addons]);

  /**
   * @function getAddons
   * @description - Function to load prepay addons
   */
  const getAddons = async () => {
    const data = await getPrepayAddons(
      setOnLoadError,
      setErrorModalOccurence,
      errorModal,
      onLoadError,
      openAPIFailureModal,
      setOpenError,
      setErrorModalOccurence,
      errorModalOccurence,
      errorModalMaxOccurence
    );
    setAddons(data);
    setLoading(false);
  };

  useEffect(() => {
    getAddons();
    dispatch({ type: 'ENABLE_FOOTER', payload: true });
  }, []);

  useEffect(() => {
    onLoadPrepayAddons(rootState, setOfferSelected, dispatch, graphql);
  }, [rootState]);

  return (
    <Container id='offerCardData'>
      {!loading && addons?.length > 0 ? (
        <Box
          sx={{
            ...(addons?.length === 1 ? styles(theme).singleOfferCard : styles(theme).offerCards),
            mt: '2rem',
          }}
          id='offerCardData'
        >
          {!md && !leftArrowDisabled && addons?.length > 3 && (
            <IconButton
              size='small'
              sx={styles(theme).prevArrow}
              onClick={handleLeftClick}
              disableRipple
              disableTouchRipple
            >
              <KeyboardArrowLeftIcon />
            </IconButton>
          )}
          <SwiperCarousel
            settings={{
              className: 'content-carousel-mobile',
              pagination: { enable: true, clickable: true },
              modules: [Pagination],
              initialSlide: 0,
              updateOnWindowResize: true,
              slidesPerView: 'auto',
              slidesPerGroup: addons?.length > 5 && !md ? 3 : 1,
              onSlideChange: swiper => {
                checkArrowsStatus();
                updatePaginationDots(swiper);
              },
              breakpoints: {
                320: {
                  slidesPerView: 1,
                },
                600: {
                  slidesPerView: 2,
                },
                1200: {
                  slidesPerView: 3,
                },
              },
              onAfterInit: swiper => {
                setTimeout(() => {
                  updatePaginationDots(swiper);
                }, 100);
              },
              onPaginationUpdate: swiper => {
                updatePaginationDots(swiper);
              },
              onBreakpoint: swiper => {
                updatePaginationDots(swiper);
              },
            }}
            sliderRef={swiperRef}
            style={{
              ...style.offerSlider.swiper,
              ...(addons?.length < 3 && style.offerSlider.centerSwiper),
            }}
          >
            {addons &&
              addons?.length > 0 &&
              addons?.map(
                (offer, index) =>
                  ((addons?.length > 0 && xs) || addons?.length > 1) && (
                    <SwiperSlide key={index}>
                      <EirOfferCard
                        slideNumber={index}
                        {...offer}
                        headerImage={offer?.image}
                        headerImgDescription={offer.headerImgDescription}
                        afterPrice={offer.afterPrice}
                        afterPricePosition={offer.afterPricePosition?.[0]}
                        moreInfo={offer.moreInfo}
                        index={index}
                        offer={offer}
                        selectButton={{
                          title: 'Select Plan',
                          action: () =>
                            offer.code === offerSelected
                              ? removeAddOn(
                                  graphql,
                                  offer.code,
                                  rootState,
                                  setShowLoader,
                                  setOfferSelected,
                                  dispatch,
                                  setOnLoadError,
                                  setErrorModalOccurence,
                                  errorModal,
                                  onLoadError,
                                  openAPIFailureModal,
                                  setOpenError,
                                  setErrorModalOccurence,
                                  errorModalOccurence,
                                  errorModalMaxOccurence
                                )
                              : addAddOn(
                                  graphql,
                                  offer.code,
                                  rootState,
                                  setShowLoader,
                                  setOfferSelected,
                                  dispatch,
                                  prepaySIM,
                                  router,
                                  setOnLoadError,
                                  setErrorModalOccurence,
                                  errorModal,
                                  onLoadError,
                                  openAPIFailureModal,
                                  setOpenError,
                                  setErrorModalOccurence,
                                  errorModalOccurence,
                                  errorModalMaxOccurence
                                ),
                        }}
                        color='primary'
                        useMaxHeight={useMaxHeight}
                        isPrepayMobile
                        code={offer.code}
                        showLoader={showLoader}
                        offerSelected={offer.code === offerSelected}
                      />
                    </SwiperSlide>
                  )
              )}
          </SwiperCarousel>
          {addons &&
            addons?.length > 0 &&
            addons?.length === 1 &&
            !xs &&
            addons?.map((offer, index) => (
              <EirSingleOfferCard
                {...offer}
                singleTariffHeaderImage={offer.singleTariffImage}
                singleHeaderImgDescription={offer.singleHeaderImgDescription}
                index={index}
                offer={offer}
                selectButton={{
                  title: 'Select Plan',
                  action: () =>
                    offer.code === offerSelected
                      ? removeAddOn(
                          graphql,
                          offer.code,
                          rootState,
                          setShowLoader,
                          setOfferSelected,
                          dispatch,
                          setOnLoadError,
                          setErrorModalOccurence,
                          errorModal,
                          onLoadError,
                          openAPIFailureModal,
                          setOpenError,
                          setErrorModalOccurence,
                          errorModalOccurence,
                          errorModalMaxOccurence
                        )
                      : addAddOn(
                          graphql,
                          offer.code,
                          rootState,
                          setShowLoader,
                          setOfferSelected,
                          dispatch,
                          prepaySIM,
                          router,
                          setOnLoadError,
                          setErrorModalOccurence,
                          errorModal,
                          onLoadError,
                          openAPIFailureModal,
                          setOpenError,
                          setErrorModalOccurence,
                          errorModalOccurence,
                          errorModalMaxOccurence
                        ),
                }}
                color='primary'
                code={offer.code}
                showLoader={showLoader}
                offerSelected={offer.code === offerSelected}
              />
            ))}
          {!md && !RightArrowDisabled && addons?.length > 3 && (
            <IconButton
              size='small'
              sx={styles(theme).nextArrow}
              onClick={handleRightClick}
              disableRipple
              disableTouchRipple
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          )}
        </Box>
      ) : (
        <Stack spacing={3} direction='row' justifyContent={{ md: 'center' }}>
          {Array(2)
            .fill()
            .map((_, index) => (
              <EirOfferCard loading index={index} />
            ))}
        </Stack>
      )}
      {openError && errorModalOccurence > errorModalMaxOccurence && (
        <ApiFailureModal openAPIFailureModal={openAPIFailureModal} handleOk={handleOk} />
      )}
      {errorModal.value && !showModal.value && !showSessionExpiredModal.value && (
        <ErrorModal
          errorBBModal={errorModal}
          occurences={errorModalMaxOccurence}
          currentOccurence={errorModalOccurence}
          ctaMethod={() => {}}
        />
      )}
    </Container>
  );
};

export default PrepayMobileOfferContainer;
